// Configuration resolver for MSAL and auth-related settings
//
// Purpose:
// - Consolidate logic to derive MSAL clientId, authority, and API scopes.
// - Fetch backend public config (/api/publicconfig) and fall back to env vars.
// - Cache the resolved config so network calls are only performed once.

const baseScopes = ['openid', 'profile', 'email'];

export interface AuthConfig {
  clientId: string;
  authority: string;
  apiScope: string;
  loginScopes: string[];
}

function normalizeAuthority(authority: string): string {
  const a = (authority || '').trim();
  if (!a) return '';
  // MSAL expects a full URL. Ensure trailing slash for consistent parsing.
  try {
    const u = new URL(a);
    return u.href.endsWith('/') ? u.href : `${u.href}/`;
  } catch {
    // Not a parseable URL; return as-is so we can give a better error upstream.
    return a;
  }
}

function getDefaultAuthority(): string {
  // Safe-ish default for single-tenant AAD if tenant isn't provided elsewhere.
  // If you're using CIAM/B2C, the backend should provide the correct authority.
  return 'https://login.microsoftonline.com/common/';
}

let cachedConfig: AuthConfig | null = null;
let resolving: Promise<AuthConfig> | null = null;

export async function resolveConfig(): Promise<AuthConfig> {
  // Return cached result if already resolved
  if (cachedConfig) return cachedConfig;
  // If a resolution is already in flight, return the same promise so callers share the network request
  if (resolving) return resolving;

  resolving = (async () => {
    let apiScope = (import.meta.env.VITE_AZURE_AD_API_SCOPE || '').trim();
    let clientId = (import.meta.env.VITE_AZURE_AD_CLIENT_ID || '').trim();
    let authority = normalizeAuthority(import.meta.env.VITE_AZURE_AD_AUTHORITY || '');

    try {
      const res = await fetch('/api/publicconfig');
      if (res.ok) {
        const json = await res.json();
        if (json.clientId) clientId = String(json.clientId);
        if (json.authority) authority = normalizeAuthority(String(json.authority));
      }
    } catch (e) {
      // Ignore and fall back to env vars
      console.warn('Failed to fetch backend config, falling back to env vars', e);
    }

    if ((!apiScope || apiScope.length === 0) && clientId) {
      apiScope = `api://${clientId}/Api.Access`;
    }

    const loginScopes = apiScope ? [...baseScopes, apiScope] : baseScopes;

    // If authority is missing, MSAL v3 can throw in CIAM authority transforms.
    // Prefer a loud warning + fallback, rather than passing an empty/undefined authority.
    if (!authority) {
      console.warn(
        'Auth config: authority is missing. Falling back to https://login.microsoftonline.com/common/. ' +
          'Set AppSettings:Authority in WebApi/appsettings*.json (or VITE_AZURE_AD_AUTHORITY) to your tenant/CIAM authority.'
      );
      authority = getDefaultAuthority();
    } else {
      // If it isn't a URL, log a useful hint and still fall back.
      try {
        // eslint-disable-next-line no-new
        new URL(authority);
      } catch {
        console.warn(
          `Auth config: authority is not a valid URL ("${authority}"). Falling back to ${getDefaultAuthority()}. ` +
            'Fix AppSettings:Authority (WebApi) or VITE_AZURE_AD_AUTHORITY (WebReact).'
        );
        authority = getDefaultAuthority();
      }
    }

    cachedConfig = { clientId, authority, apiScope, loginScopes };
    resolving = null;
    return cachedConfig;
  })();

  return resolving;
}

// Expose the cached config so other modules can access it without owning cache logic.
export function getCachedConfig(): AuthConfig | null {
  return cachedConfig;
}
